import { Tab, TabsNavigation } from 'components/molecules';
import Tabs from 'components/molecules/tabs';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';

import i18next from 'i18next';
import { useTranslation } from 'react-i18next';

import { DIGITAL_PRODUCT_TYPE, RELEASE_TYPE } from 'services/music-product-digital/types/enums';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { ARTIST_ROLE, GENRE, STATUS } from 'services/types';
import { II18nextPrefix } from 'types/components/components.interface';

import { useEnumToSelectOptions } from 'utils/components/inputs/select';

import {
  conditionalField,
  credensialsValidation,
  externalIdsValidation,
  getTabError,
  requiredEnumSelect,
  titlesValidation,
} from 'utils/global/validation.utils';

import { array, boolean, date, mixed, number, object, string } from 'yup';
import { IFormProps, IFormikProps } from '../types';

import MusicProductDigitalDelivery from './tabs/MusicProductDigitalDelivery';
import MusicProductDigitalDeliveryAll from './tabs/MusicProductDigitalDeliveryAll';
import MusicProductDigitalDeliveryDetail from './tabs/MusicProductDigitalDeliveryDetail';

import FormSubmitButtons from 'components/molecules/buttons/formSubmitButtons/FormSubmitButtons';
import { albumValidation } from 'components/molecules/tabs/tracks/TracksRoleTab';
import { FormObserver } from 'hooks/useFormObserver';
import { useStoreState } from 'providers/StoreStateContext';
import MusicProductDigitalData from './tabs/MusicProductDigitalData';
import MusicProductDigitalVideo from './tabs/MusicProductDigitalVideo';

export enum musicProductDigitalTabs {
  tracks,
  video,
  data,
  artists,
  // titles,
  external,
  contracts,
  delivery,
  statistics,
}

export const marketingInitialValues = {
  smartlink: '',
  linkName: '',
  domainName: '',
};

export const musicProductDigitalCopyrights = {
  name: '',
  year: '',
};

export const musicProductDigitalInitialValues: IMusicProductDigitalForm = {
  id: '',
  mainTitle: {
    language: -1,
    title: '',
    version: '',
  },
  releaseStatus: STATUS.draft,
  releaseType: RELEASE_TYPE.unknown,
  releaseDate: '',
  genre: { id: -1, name: '' },
  ean: '',
  assignEan: false,
  pLine: musicProductDigitalCopyrights,
  cLine: { name: '', year: '' },
  productImageLink: '',
  productImagePreviewLink: '',
  artists: [],
  externalIds: [],
  otherTitles: [],
  tracks: [],
  settlementInfo: {
    contracts: [],
    perTrackSettlement: false,
    perTrackContracts: [],
  },
  smartlink: '',
  marketing: marketingInitialValues,
  productType: DIGITAL_PRODUCT_TYPE.audio,
  videoId: '',
  video: null,
  hasDeliveryDiscrepancy: null,
  canSendDigitalDelivery: false,
  thumbnailImageLink: '',
  deliveryHasErrors: null,
  hasDuplicatedArtist: null,
  version: 0,
};

export const artistsValidation = object().shape({
  id: string().required(() => i18next.t('formik-error.required')),
  role: requiredEnumSelect(ARTIST_ROLE),
});
export const settlementInfoContractValidation = object().shape({
  contractId: string()
    .trim()
    .required(() => i18next.t('formik-error.required')),
  labelId: string().required(() => i18next.t('formik-error.required')),
  startDate: string().required(() => i18next.t('formik-error.required')),
  divisionPercentage: number().required(() => i18next.t('formik-error.required')),
});
export const settlementInfoPerTrackContractValidation = object().shape({
  contracts: array().of(settlementInfoContractValidation),
});
export const settlementInfoValidation = object().shape({
  perTrackSettlement: boolean().when('contracts', {
    is: (contracts: any[]) => !!contracts.length,
    then: (schema) => schema.required(() => i18next.t('formik-error.required')),
    otherwise: (schema) => schema.nullable(),
  }),
  contracts: array().of(settlementInfoContractValidation),
  perTrackContracts: array().of(settlementInfoPerTrackContractValidation),
});

const musicProductDigitalValidationSchema = object().shape({
  releaseStatus: requiredEnumSelect(STATUS),
  mainTitle: titlesValidation,
  pLine: credensialsValidation('releaseStatus'),
  cLine: credensialsValidation('releaseStatus'),
  assignEan: boolean(),
  ean: string().when('assignEan', {
    is: (assignEan: boolean) => assignEan === false,
    then: (schema) => schema.required(() => i18next.t('formik-error.required')),
    otherwise: (schema) => schema.nullable().optional(),
  }),
  releaseType: conditionalField(requiredEnumSelect(RELEASE_TYPE), 'releaseStatus'),
  releaseDate: conditionalField(
    date().required(() => i18next.t('formik-error.required')),
    'releaseStatus',
  ),

  genre: conditionalField(object().shape({ id: requiredEnumSelect(GENRE) }), 'releaseStatus'),
  artists: array()
    .of(artistsValidation)
    .when('releaseStatus', {
      is: (status?: STATUS) => status !== STATUS.draft && status !== STATUS.unknown,
      then: (schema) =>
        schema
          .min(1, () => i18next.t('formik-error.at-least-one-artist-required'))
          .test(
            'has-mpd-main-artist',
            () =>
              i18next.t('formik-error.what-should-have-main-artist', {
                what: i18next.t('track.what'),
              }),
            (artist) => artist && artist.some((item) => item.role === ARTIST_ROLE.mainArtist),
          ),
      otherwise: (schema) => schema,
    }),
  otherTitles: array().of(titlesValidation),
  externalIds: array().of(externalIdsValidation),
  tracks: array().when('productType', {
    is: (productType: DIGITAL_PRODUCT_TYPE) => productType === DIGITAL_PRODUCT_TYPE.audio,
    then: (schema) =>
      schema.of(albumValidation).when('releaseStatus', {
        is: (status?: STATUS) => status !== STATUS.draft && status !== STATUS.unknown,
        then: (schema) =>
          schema
            .min(1, () => i18next.t('formik-error.at-least-one-album-required'))
            .required(() => i18next.t('formik-error.required')),
        otherwise: (schema) => schema,
      }),
    otherwise: (schema) => schema.nullable().optional(),
  }),
  productImageLink: string().nullable(),
  file: conditionalField(
    mixed().when('productImageLink', {
      is: (value?: string) => !!value?.length,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required(() => i18next.t('formik-error.required')),
    }),
    'releaseStatus',
  ),
});

export interface IMusicProductDigitalFormProps
  extends
    Omit<FormikConfig<IMusicProductDigitalForm>, 'initialValues'>,
    IFormProps,
    II18nextPrefix {
  initialValues?: IMusicProductDigitalForm;
}

const MusicProductDigitalForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  status,
  initialValues = musicProductDigitalInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  validationSchema = musicProductDigitalValidationSchema,
  ...props
}: IMusicProductDigitalFormProps) => {
  const { t } = useTranslation();
  const { getStatusOptions } = useEnumToSelectOptions();
  const { setForm } = useStoreState();

  return (
    <Formik
      {...props}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      initialValues={initialValues}
    >
      {({
        setFieldValue,
        values,
        status,
        errors,
        handleBlur,
      }: IFormikProps<IMusicProductDigitalForm>) => (
        <Form className="w-full h-full">
          <FormObserver<IMusicProductDigitalForm>
            onChange={(newValues) => {
              setForm('musicProductDigitalForm', newValues);
            }}
          />
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={tittle}
              path={backPath ?? 'music-product-digital'}
              topbarContent={
                <div className="flex w-full items-center justify-end">
                  <div className="flex items-center gap-2 w-full justify-end">
                    {/* {!!initialValues.mainTitle.title.length && status?.displayMode && (
                      <LabeledStatus status={initialValues.releaseStatus} noname />
                    )} */}
                    {!status?.displayMode && <FormSubmitButtons statusName="releaseStatus" />}
                    {topbarContent}
                  </div>
                </div>
              }
            >
              <TabsNavigation
                displayMode={status?.displayMode}
                tabs={[
                  {
                    value: musicProductDigitalTabs.data,
                    name: 'data',
                    error: getTabError([
                      errors.mainTitle?.title,
                      errors.mainTitle?.version,
                      errors.mainTitle?.language,
                      errors.ean,
                      errors.productType,
                      errors.releaseType,
                      errors.releaseDate,
                      errors.genre?.id,
                      errors.productImageLink,
                      errors.file,
                      errors.pLine,
                      errors.cLine,
                      errors.assignEan,
                    ]),
                  },
                  ...(values.productType === DIGITAL_PRODUCT_TYPE.audio
                    ? [
                        {
                          value: musicProductDigitalTabs.tracks,
                          error: errors.tracks,
                          name: 'tracks',
                        },
                      ]
                    : []),
                  ...(values.productType === DIGITAL_PRODUCT_TYPE.video
                    ? [
                        {
                          value: musicProductDigitalTabs.video,
                          error: errors.video,
                          name: 'video',
                        },
                      ]
                    : []),
                  {
                    value: musicProductDigitalTabs.artists,
                    error:
                      errors.artists ||
                      (!!values.hasDuplicatedArtist
                        ? t('toast-message.duplicated-artist-error')
                        : undefined),
                    name: 'artists',
                  },

                  {
                    value: musicProductDigitalTabs.contracts,
                    name: 'contracts',
                    hideInEditMode: true,
                  },

                  {
                    value: musicProductDigitalTabs.delivery,
                    name: 'delivery',
                  },

                  // {
                  //   value: musicProductDigitalTabs.titles,
                  //   error: errors.otherTitles,
                  //   name: 'titles',
                  // },
                  {
                    value: musicProductDigitalTabs.external,
                    error: errors.externalIds,
                    name: 'external',
                    hideInEditMode: true,
                  },
                ]}
                disabled={disableTabs}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

MusicProductDigitalForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    {
      path: 'tracks',
      element: <Tabs.TracksStep parent="MUSIC_PRODUCT_DIGITAL" />,
    },
    { path: 'artists', element: <Tabs.ArtistsRoleStep parent="MUSIC_PRODUCT_DIGITAL" /> },
    // { path: 'titles', element: <Tabs.Titles parent="MUSIC_PRODUCT_DIGITAL" /> },
    { path: 'contracts', element: <Tabs.ContractsPerTrack /> },
    { path: 'external', element: <Tab.ExternalId parent="MUSIC_PRODUCT_DIGITAL" /> },
    { path: 'video', element: <MusicProductDigitalVideo /> },
    { path: 'data', element: <MusicProductDigitalData /> },
    {
      path: 'delivery',
      element: <MusicProductDigitalDelivery />,
    },
    {
      path: 'delivery/all',
      element: <MusicProductDigitalDeliveryAll />,
    },
    {
      path: 'delivery/:salesChannelId/:providerId',
      element: <MusicProductDigitalDeliveryDetail />,
    },
  ].filter((children) => !disabledChildren?.includes(children.path));

export default MusicProductDigitalForm;
